"use client";

import * as React from "react";
import { cn } from "@repo/utils";
import { Input } from "./input";
import { Label } from "./label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";

export interface CreditCardData {
  cardNumber: string;
  cardHolder: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
}

interface CreditCardInputProps {
  value?: CreditCardData;
  onChange?: (value: CreditCardData) => void;
  className?: string;
  disabled?: boolean;
}

const defaultCardData: CreditCardData = {
  cardNumber: "",
  cardHolder: "",
  expiryMonth: "",
  expiryYear: "",
  cvv: "",
};

// Generate year options (current year + 20 years)
const currentYear = new Date().getFullYear();
const years = Array.from({ length: 21 }, (_, i) => (currentYear + i).toString());
const months = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, "0"));

// Detect card type from number
const getCardType = (number: string): "visa" | "mastercard" | "amex" | "unknown" => {
  const cleaned = number.replace(/\s/g, "");
  if (/^4/.test(cleaned)) return "visa";
  if (/^5[1-5]/.test(cleaned) || /^2[2-7]/.test(cleaned)) return "mastercard";
  if (/^3[47]/.test(cleaned)) return "amex";
  return "unknown";
};

const CreditCardInput = React.forwardRef<HTMLDivElement, CreditCardInputProps>(
  ({ value = defaultCardData, onChange, className, disabled = false }, ref) => {
    const [cardData, setCardData] = React.useState<CreditCardData>(value);
    const [showBack, setShowBack] = React.useState(false);

    React.useEffect(() => {
      setCardData(value);
    }, [value]);

    const handleChange = (field: keyof CreditCardData, val: string) => {
      const newData = { ...cardData, [field]: val };
      setCardData(newData);
      onChange?.(newData);
    };

    const formatCardNumber = (num: string) => {
      const cleaned = num.replace(/\D/g, "").slice(0, 16);
      const groups = cleaned.match(/.{1,4}/g);
      return groups ? groups.join(" ") : cleaned;
    };

    const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const formatted = formatCardNumber(e.target.value);
      handleChange("cardNumber", formatted);
    };

    const handleCardHolderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      handleChange("cardHolder", e.target.value.toUpperCase());
    };

    const handleCvvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const cardType = getCardType(cardData.cardNumber);
      const maxLength = cardType === "amex" ? 4 : 3;
      const cleaned = e.target.value.replace(/\D/g, "").slice(0, maxLength);
      handleChange("cvv", cleaned);
    };

    const cardType = getCardType(cardData.cardNumber);
    const cvvLength = cardType === "amex" ? 4 : 3;

    return (
      <div ref={ref} className={cn("space-y-4", className)}>
        {/* Visual Card */}
        <div
          className={cn(
            "relative w-full max-w-[350px] h-[200px] rounded-xl p-6 text-white transition-transform duration-500",
            showBack ? "bg-gradient-to-br from-gray-700 to-gray-900" : "bg-gradient-to-br from-blue-600 to-purple-700",
            disabled && "opacity-50"
          )}
          style={{ perspective: "1000px" }}
        >
          {!showBack ? (
            // Front of card
            <div className="flex flex-col justify-between h-full">
              <div className="flex justify-between items-start">
                <div className="w-12 h-8 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded" />
                <span className="text-sm font-semibold uppercase">
                  {cardType !== "unknown" ? cardType : "Card"}
                </span>
              </div>
              <div className="text-xl font-mono tracking-wider">
                {cardData.cardNumber || "•••• •••• •••• ••••"}
              </div>
              <div className="flex justify-between items-end">
                <div>
                  <div className="text-xs text-gray-300">Titulaire</div>
                  <div className="font-medium">{cardData.cardHolder || "NOM PRENOM"}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-300">Expire</div>
                  <div className="font-medium">
                    {cardData.expiryMonth || "MM"}/{cardData.expiryYear?.slice(-2) || "YY"}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Back of card
            <div className="flex flex-col h-full">
              <div className="w-full h-10 bg-black mt-4" />
              <div className="flex items-center justify-end mt-4 pr-4">
                <div className="bg-gray-200 text-black px-3 py-1 font-mono">
                  {cardData.cvv || "•".repeat(cvvLength)}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Form Fields */}
        <div className="space-y-3">
          <div>
            <Label htmlFor="cardNumber">Numéro de carte</Label>
            <Input
              id="cardNumber"
              placeholder="1234 5678 9012 3456"
              value={cardData.cardNumber}
              onChange={handleCardNumberChange}
              disabled={disabled}
              maxLength={19}
            />
          </div>

          <div>
            <Label htmlFor="cardHolder">Titulaire</Label>
            <Input
              id="cardHolder"
              placeholder="NOM PRENOM"
              value={cardData.cardHolder}
              onChange={handleCardHolderChange}
              disabled={disabled}
            />
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <Label>Expiration</Label>
              <div className="flex gap-2">
                <Select
                  value={cardData.expiryMonth}
                  onValueChange={(val) => handleChange("expiryMonth", val)}
                  disabled={disabled}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="MM" />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((month) => (
                      <SelectItem key={month} value={month}>
                        {month}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={cardData.expiryYear}
                  onValueChange={(val) => handleChange("expiryYear", val)}
                  disabled={disabled}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="YYYY" />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((year) => (
                      <SelectItem key={year} value={year}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="w-24">
              <Label htmlFor="cvv">CVV</Label>
              <Input
                id="cvv"
                placeholder={cardType === "amex" ? "••••" : "•••"}
                value={cardData.cvv}
                onChange={handleCvvChange}
                onFocus={() => setShowBack(true)}
                onBlur={() => setShowBack(false)}
                disabled={disabled}
                maxLength={cvvLength}
                type="password"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
);

CreditCardInput.displayName = "CreditCardInput";

export { CreditCardInput };
